import React, { useState } from 'react';
import '../components/Signup/Signup.css';
import { useSelector, useDispatch } from 'react-redux';
import { useCompany } from '../hooks/useApiHooks';
import toast from 'react-hot-toast';
import { useLanguage, getUserManagementTranslation } from '../hooks/useLanguage';

function UserManagement() {
  useCompany();
  const userData = useSelector((state) => state.user.userData);
  const company = useSelector(state => state.company);
  const dispatch = useDispatch();
  const [isSpanish] = useLanguage();

  // Separate editing states for user and company
  const [isEditingUser, setIsEditingUser] = useState(false);
  const [isEditingCompany, setIsEditingCompany] = useState(false);
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);

  // Separate original data states
  const [originalUserData, setOriginalUserData] = useState(userData);
  const [originalCompanyData, setOriginalCompanyData] = useState(company);

  // Add state for loading and error handling
  const [userUpdateLoading, setUserUpdateLoading] = useState(false);
  const [userUpdateError, setUserUpdateError] = useState(null);
  const [companyUpdateLoading, setCompanyUpdateLoading] = useState(false);
  const [companyUpdateError, setCompanyUpdateError] = useState(null);

  // Handle user data changes
  const handleUserChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: 'UPDATE_USER', payload: { ...userData, [name]: value } });
  };

  // Handle company data changes
  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: 'UPDATE_COMPANY', payload: { ...company, [name]: value } });
  };

  // User edit functions
  const handleEditUser = () => {
    setIsEditingUser(true);
    setOriginalUserData(userData);
  };

  const handleCancelUser = () => {
    setIsEditingUser(false);
    dispatch({ type: 'UPDATE_USER', payload: originalUserData });
    setUserUpdateError(null);
  };

  // Company edit functions
  const handleEditCompany = () => {
    setIsEditingCompany(true);
    setOriginalCompanyData(company);
  };

  const handleCancelCompany = () => {
    setIsEditingCompany(false);
    dispatch({ type: 'UPDATE_COMPANY', payload: originalCompanyData });
    setCompanyUpdateError(null);
  };

  // User form submission with API call
  const handleSubmitUser = async (e) => {
    e.preventDefault();
    setUserUpdateLoading(true);
    setUserUpdateError(null);

    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const requestBody = {
        email: userData?.email,
        first_name: userData?.first_name,
        last_name: userData?.last_name
      };

      const response = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update user details');
      }

      const updatedUser = await response.json();
      toast.success(getUserManagementTranslation('successUserUpdate', isSpanish));

      dispatch({ type: 'UPDATE_USER', payload: updatedUser });

      // Exit editing mode
      setIsEditingUser(false);
    } catch (error) {
      toast.error(getUserManagementTranslation('errorUpdatingUser', isSpanish));
      console.error('Error updating user:', error.message);
      setUserUpdateError(error.message);
    } finally {
      setUserUpdateLoading(false);
    }
  };

  // Company form submission
  const handleSubmitCompany = async (e) => {
    e.preventDefault();
    setCompanyUpdateLoading(true);
    setCompanyUpdateError(null);

    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const userId = sessionStorage.getItem('user_id');
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/company`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          nombre: company.nombre,
          nif: company.nif,
          eori: company.eori,
          direccion: company.direccion,
          localidad: company.localidad,
          pais: company.pais,
          codigo_postal: company.codigo_postal,
          aduana_habitual: company.aduana_habitual,
          correo_electronico: company.correo_electronico
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update company details');
      }

      const updatedCompany = await response.json();
      toast.success(getUserManagementTranslation('successCompanyUpdate', isSpanish));

      // Update Redux store with the response from server
      dispatch({ type: 'UPDATE_COMPANY', payload: updatedCompany.data });

      // Exit editing mode
      setIsEditingCompany(false);
    } catch (error) {
      toast.error(getUserManagementTranslation('errorUpdatingCompany', isSpanish));
      console.error('Error updating company:', error.message);
      setCompanyUpdateError(error.message);
    } finally {
      setCompanyUpdateLoading(false);
    }
  };

  // Add new function to handle company creation
  const handleCreateCompany = async () => {
    setCompanyUpdateLoading(true);
    setCompanyUpdateError(null);
    
    try {
      const token = sessionStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const companyData = {
        nombre: company.nombre,
        nif: company.nif,
        eori: company.eori,
        direccion: company.direccion,
        localidad: company.localidad,
        pais: company.pais,
        codigo_postal: company.codigo_postal,
        aduana_habitual: company.aduana_habitual,
        correo_electronico: company.correo_electronico
      };
      
      const response = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/company`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(companyData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create company');
      }
      
      toast.success(getUserManagementTranslation('successCompanyCreate', isSpanish));
      
      // Use the input data as the payload since there's no response
      dispatch({ type: 'SET_COMPANY', payload: companyData });
      dispatch({ type: 'SET_NEEDS_COMPANY_CREATION', payload: false });
      setIsCreatingCompany(false);
      
    } catch (error) {
      toast.error(getUserManagementTranslation('errorCreatingCompany', isSpanish));
      console.error('Error creating company:', error.message);
      setCompanyUpdateError(error.message);
    } finally {
      setCompanyUpdateLoading(false);
    }
  };

  // Check for changes in each form
  const hasUserChanges = JSON.stringify(userData) !== JSON.stringify(originalUserData);
  const hasCompanyChanges = JSON.stringify(company) !== JSON.stringify(originalCompanyData);

  return (
    <div className="text-left">
      <div className='flex justify-between gap-4 mb-20 items-center'>
        <h1 className='text-3xl font-bold'>{getUserManagementTranslation('userProfileManagement', isSpanish)}</h1>
      </div>

      {/* User Details Form */}
      <form className="mb-4" onSubmit={handleSubmitUser}>
        <div className='p-8 border-container rounded-2xl form'>
          <div className='flex justify-between gap-4 mb-8 items-center'>
            <h1 className='text-xl font-bold'>{getUserManagementTranslation('userDetails', isSpanish)}</h1>
            <div className='flex justify-end items-center gap-2'>
              {isEditingUser ? (
                <>
                  <span type="button" onClick={handleCancelUser} className='text-sm !bg-white mr-2 cursor-pointer'>
                    {getUserManagementTranslation('cancel', isSpanish)}
                  </span>
                  <button
                    type="submit"
                    className={`text-sm login-button py-2 rounded-full px-5 mr-0 w-full ${(!hasUserChanges || userUpdateLoading) && "opacity-50"}`}
                    disabled={!hasUserChanges || userUpdateLoading}
                  >
                    {userUpdateLoading ? getUserManagementTranslation('saving', isSpanish) : getUserManagementTranslation('save', isSpanish)}
                  </button>
                </>
              ) : (
                <button type="button" onClick={handleEditUser} className='bg-gray-100 rounded-full py-2 px-5 mr-0 w-full text-sm'>
                  {getUserManagementTranslation('edit', isSpanish)}
                </button>
              )}
            </div>
          </div>

          {userUpdateError && <div className="text-red-500 mb-4">{userUpdateError}</div>}

          <div className='flex gap-4'>
            <div className="form-group flex-1">
              <label htmlFor="first_name">{getUserManagementTranslation('firstName', isSpanish)}</label>
              {isEditingUser ? (
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  className="form-input border-container-force"
                  value={userData?.first_name}
                  onChange={handleUserChange}
                  required
                />
              ) : (
                <p className='py-2 text-xl font-bold'>{userData?.first_name}</p>
              )}
            </div>
            <div className="form-group flex-1">
              <label htmlFor="last_name">{getUserManagementTranslation('lastName', isSpanish)}</label>
              {isEditingUser ? (
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  className="form-input border-container-force"
                  value={userData.last_name}
                  onChange={handleUserChange}
                  required
                />
              ) : (
                <p className='py-2 text-xl font-bold'>{userData.last_name}</p>
              )}
            </div>
          </div>
          <div className='flex gap-4'>
            <div className="form-group flex-1">
              <label htmlFor="email">{getUserManagementTranslation('email', isSpanish)}</label>
              {isEditingUser ? (
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input border-container-force"
                  value={userData.email}
                  onChange={handleUserChange}
                  required
                />
              ) : (
                <p className='py-2 text-xl font-bold'>{userData.email}</p>
              )}
            </div>
          </div>
        </div>
      </form>

      {/* Company Details Form */}
      {company.needsCompanyCreation ? (
        <div className='p-8 border-container rounded-2xl form'>
          <div className='flex justify-between gap-4 items-center'>
            <h1 className='text-xl font-bold'>{getUserManagementTranslation('companyDetails', isSpanish)}</h1>

            {isCreatingCompany ? (
              <div className='flex items-center gap-2'>
                <span type="button" onClick={() => setIsCreatingCompany(false)} className='text-sm !bg-white mr-2 cursor-pointer'>
                  {getUserManagementTranslation('cancel', isSpanish)}
                </span>
                <button
                  type="button"
                  onClick={handleCreateCompany}
                  className={`text-sm login-button py-2 rounded-full px-5 mr-0 ${companyUpdateLoading && "opacity-50"}`}
                  disabled={companyUpdateLoading}
                >
                  {companyUpdateLoading ? getUserManagementTranslation('creating', isSpanish) : getUserManagementTranslation('createCompany', isSpanish)}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsCreatingCompany(true)}
                className={`text-sm login-button py-2 px-5 rounded-full mr-0 ${companyUpdateLoading && "opacity-50"}`}
                disabled={companyUpdateLoading}
              >
                {getUserManagementTranslation('createCompany', isSpanish)}
              </button>
            )}
          </div>


          {isCreatingCompany && (
          <>
          {companyUpdateError && <div className="text-red-500 mb-4">{companyUpdateError}</div>}

          <div className='flex gap-4 mt-8'>
            <div className="form-group flex-1">
              <label htmlFor="nombre">{getUserManagementTranslation('companyName', isSpanish)}</label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                className="form-input border-container-force"
                value={company.nombre}
                onChange={handleCompanyChange}
                required
              />
            </div>
            <div className="form-group flex-1">
              <label htmlFor="correo_electronico">{getUserManagementTranslation('companyEmail', isSpanish)}</label>
              <input
                type="text"
                id="correo_electronico"
                name="correo_electronico"
                className="form-input border-container-force"
                value={company.correo_electronico}
                onChange={handleCompanyChange}
                required
              />
            </div>
          </div>

          <div className='flex gap-4'>
            <div className="form-group flex-1">
              <label htmlFor="nif">{getUserManagementTranslation('nif', isSpanish)}</label>
              <input
                type="text"
                id="nif"
                name="nif"
                className="form-input border-container-force"
                value={company.nif}
                onChange={handleCompanyChange}
                required
              />
            </div>
            <div className="form-group flex-1">
              <label htmlFor="eori">{getUserManagementTranslation('eoriNumber', isSpanish)}</label>
              <input
                type="text"
                id="eori"
                name="eori"
                className="form-input border-container-force"
                value={company.eori}
                onChange={handleCompanyChange}
                required
              />
            </div>
          </div>

          <div className="form-group flex-1">
            <label htmlFor="direccion">{getUserManagementTranslation('address', isSpanish)}</label>
            <input
              type="text"
              id="direccion"
              name="direccion"
              className="form-input border-container-force"
              value={company.direccion}
              onChange={handleCompanyChange}
              required
            />
          </div>

          <div className='flex gap-4'>
            <div className="form-group flex-1">
              <label htmlFor="localidad">{getUserManagementTranslation('city', isSpanish)}</label>
              <input
                type="text"
                id="localidad"
                name="localidad"
                className="form-input border-container-force"
                value={company.localidad}
                onChange={handleCompanyChange}
                required
              />
            </div>
            <div className="form-group flex-1">
              <label htmlFor="codigo_postal">{getUserManagementTranslation('postalCode', isSpanish)}</label>
              <input
                type="text"
                id="codigo_postal"
                name="codigo_postal"
                className="form-input border-container-force"
                value={company.codigo_postal}
                onChange={handleCompanyChange}
                required
              />
            </div>
          </div>

          <div className='flex gap-4'>
            <div className="form-group flex-1">
              <label htmlFor="pais">{getUserManagementTranslation('country', isSpanish)}</label>
              <input
                type="text"
                id="pais"
                name="pais"
                className="form-input border-container-force"
                value={company.pais}
                onChange={handleCompanyChange}
                required
              />
            </div>
            <div className="form-group flex-1">
              <label htmlFor="aduana_habitual">{getUserManagementTranslation('standardCustomsOffice', isSpanish)}</label>
              <input
                type="text"
                id="aduana_habitual"
                name="aduana_habitual"
                className="form-input border-container-force"
                value={company.aduana_habitual}
                onChange={handleCompanyChange}
              />
            </div>
          </div>
          </>
          )}
        </div>
      ) : (
        <form className="" onSubmit={handleSubmitCompany}>
          <div className='p-8 border-container rounded-2xl form'>
            <div className='flex justify-between gap-4 mb-8 items-center'>
              <h1 className='text-xl font-bold'>{getUserManagementTranslation('companyDetails', isSpanish)}</h1>
              <div className='flex justify-end items-center gap-2'>
                {isEditingCompany ? (
                  <>
                    <span type="button" onClick={handleCancelCompany} className='text-sm !bg-white mr-2 cursor-pointer'>
                      {getUserManagementTranslation('cancel', isSpanish)}
                    </span>
                    <button
                      type="submit"
                      className={`text-sm login-button py-2 px-5 rounded-full mr-0 w-full ${(!hasCompanyChanges || companyUpdateLoading) && "opacity-50"}`}
                      disabled={!hasCompanyChanges || companyUpdateLoading}
                    >
                      {companyUpdateLoading ? getUserManagementTranslation('saving', isSpanish) : getUserManagementTranslation('save', isSpanish)}
                    </button>
                  </>
                ) : (
                  <button type="button" onClick={handleEditCompany} className='bg-gray-100 rounded-full py-2 px-5 mr-0 w-full text-sm'>
                    {getUserManagementTranslation('edit', isSpanish)}
                  </button>
                )}
              </div>
            </div>

            {companyUpdateError && <div className="text-red-500 mb-4">{companyUpdateError}</div>}

            <div className='flex gap-4'>
              <div className="form-group flex-1">
                <label htmlFor="nombre">{getUserManagementTranslation('companyName', isSpanish)}</label>
                {isEditingCompany ? (
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    className="form-input border-container-force"
                    value={company.nombre}
                    onChange={handleCompanyChange}
                    required
                  />
                ) : (
                  <p className='py-2 text-xl font-bold'>{company.nombre}</p>
                )}
              </div>
              <div className="form-group flex-1">
                <label htmlFor="correo_electronico">{getUserManagementTranslation('companyEmail', isSpanish)}</label>
                {isEditingCompany ? (
                  <input
                    type="text"
                    id="correo_electronico"
                    name="correo_electronico"
                    className="form-input border-container-force"
                    value={company.correo_electronico}
                    onChange={handleCompanyChange}
                    required
                  />
                ) : (
                  <p className='py-2 text-xl font-bold'>{company.correo_electronico}</p>
                )}
              </div>
            </div>

            <div className='flex gap-4'>
              <div className="form-group flex-1">
                <label htmlFor="nif">{getUserManagementTranslation('nif', isSpanish)}</label>
                {isEditingCompany ? (
                  <input
                    type="text"
                    id="nif"
                    name="nif"
                    className="form-input border-container-force"
                    value={company.nif}
                    onChange={handleCompanyChange}
                    required
                  />
                ) : (
                  <p className='py-2 text-xl font-bold'>{company.nif}</p>
                )}
              </div>
              <div className="form-group flex-1">
                <label htmlFor="eori">{getUserManagementTranslation('eoriNumber', isSpanish)}</label>
                {isEditingCompany ? (
                  <input
                    type="text"
                    id="eori"
                    name="eori"
                    className="form-input border-container-force"
                    value={company.eori}
                    onChange={handleCompanyChange}
                    required
                  />
                ) : (
                  <p className='py-2 text-xl font-bold'>{company.eori}</p>
                )}
              </div>
            </div>

            <div className="form-group flex-1">
              <label htmlFor="direccion">{getUserManagementTranslation('address', isSpanish)}</label>
              {isEditingCompany ? (
                <input
                  type="text"
                  id="direccion"
                  name="direccion"
                  className="form-input border-container-force"
                  value={company.direccion}
                  onChange={handleCompanyChange}
                  required
                />
              ) : (
                <p className='py-2 text-xl font-bold'>{company.direccion}</p>
              )}
            </div>

            <div className='flex gap-4'>
              <div className="form-group flex-1">
                <label htmlFor="localidad">{getUserManagementTranslation('city', isSpanish)}</label>
                {isEditingCompany ? (
                  <input
                    type="text"
                    id="localidad"
                    name="localidad"
                    className="form-input border-container-force"
                    value={company.localidad}
                    onChange={handleCompanyChange}
                    required
                  />
                ) : (
                  <p className='py-2 text-xl font-bold'>{company.localidad}</p>
                )}
              </div>
              <div className="form-group flex-1">
                <label htmlFor="codigo_postal">{getUserManagementTranslation('postalCode', isSpanish)}</label>
                {isEditingCompany ? (
                  <input
                    type="text"
                    id="codigo_postal"
                    name="codigo_postal"
                    className="form-input border-container-force"
                    value={company.codigo_postal}
                    onChange={handleCompanyChange}
                    required
                  />
                ) : (
                  <p className='py-2 text-xl font-bold'>{company.codigo_postal}</p>
                )}
              </div>
            </div>

            <div className='flex gap-4'>
              <div className="form-group flex-1">
                <label htmlFor="pais">{getUserManagementTranslation('country', isSpanish)}</label>
                {isEditingCompany ? (
                  <input
                    type="text"
                    id="pais"
                    name="pais"
                    className="form-input border-container-force"
                    value={company.pais}
                    onChange={handleCompanyChange}
                    required
                  />
                ) : (
                  <p className='py-2 text-xl font-bold'>{company.pais}</p>
                )}
              </div>
              <div className="form-group flex-1">
                <label htmlFor="aduana_habitual">{getUserManagementTranslation('standardCustomsOffice', isSpanish)}</label>
                {isEditingCompany ? (
                  <input
                    type="text"
                    id="aduana_habitual"
                    name="aduana_habitual"
                    className="form-input border-container-force"
                    value={company.aduana_habitual}
                    onChange={handleCompanyChange}
                  />
                ) : (
                  <p className='py-2 text-xl font-bold'>{company.aduana_habitual}</p>
                )}
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

export default UserManagement;